'use client';

import { useEffect, useState } from 'react';

type SubmitState =
  | { type: 'idle' }
  | { type: 'submitting' }
  | { type: 'success'; message: string }
  | { type: 'error'; message: string };

const initialState: SubmitState = { type: 'idle' };

export default function SupportForm() {
  const [state, setState] = useState<SubmitState>(initialState);
  const [startedAt, setStartedAt] = useState('');
  useEffect(() => {
    setStartedAt(String(Date.now()));
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState({ type: 'submitting' });

    const form = event.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('/api/support', {
        method: 'POST',
        body: formData,
      });

      const payload = (await response.json().catch(() => null)) as {
        ok?: boolean;
        message?: string;
      } | null;

      if (!response.ok || !payload?.ok) {
        setState({
          type: 'error',
          message:
            payload?.message ||
            'The message could not be sent right now. Please try again in a moment.',
        });
        return;
      }

      form.reset();
      setState({
        type: 'success',
        message:
          payload.message ||
          'Message sent. The Cleanor Labs team will reply from support@cleanor.app.',
      });
    } catch {
      setState({
        type: 'error',
        message: 'The request failed. Please try again in a moment.',
      });
    }
  }

  return (
    <form className="support-form" onSubmit={handleSubmit}>
      <input type="hidden" name="startedAt" value={startedAt} />
      <div className="support-form-honeypot" aria-hidden="true">
        <label htmlFor="company">Company</label>
        <input autoComplete="off" id="company" name="company" tabIndex={-1} type="text" />
      </div>

      <div className="support-form-grid">
        <label className="field">
          <span>Name</span>
          <input
            autoComplete="name"
            maxLength={80}
            minLength={2}
            name="name"
            placeholder="Your name"
            required
            type="text"
          />
        </label>

        <label className="field">
          <span>Email</span>
          <input
            autoComplete="email"
            maxLength={120}
            name="email"
            placeholder="you@example.com"
            required
            type="email"
          />
        </label>
      </div>

      <div className="support-form-grid">
        <label className="field">
          <span>Subject</span>
          <input
            maxLength={120}
            minLength={4}
            name="subject"
            placeholder="What do you need help with?"
            required
            type="text"
          />
        </label>

        <label className="field">
          <span>Product</span>
          <select defaultValue="general" name="product">
            <option value="general">General</option>
            <option value="cleanor-ios">Cleanor for iPhone</option>
            <option value="cleanor">Cleanor for Android</option>
            <option value="website">Website</option>
          </select>
        </label>
      </div>

      <label className="field field-textarea">
        <span>Message</span>
        <textarea
          maxLength={2000}
          minLength={30}
          name="message"
          placeholder="Describe the issue, question, or request."
          required
          rows={8}
        />
      </label>

      <div className="support-form-footer">
        <p className="support-form-note">
          This form is protected with a honeypot field, timing checks, and server-side rate
          limiting.
        </p>
        <button className="button" disabled={state.type === 'submitting'} type="submit">
          {state.type === 'submitting' ? 'Sending...' : 'Send message'}
        </button>
      </div>

      {state.type === 'success' ? (
        <p className="support-feedback support-feedback-success">{state.message}</p>
      ) : null}
      {state.type === 'error' ? (
        <p className="support-feedback support-feedback-error">{state.message}</p>
      ) : null}
    </form>
  );
}
